/**
 * The side-shape catalogue.
 *
 * Every shape is drawn as the START side — the one that sits before the content
 * and cups it from the left. The END side is this same path flipped in CSS,
 * never a second hand-drawn asset. That single convention is what keeps the two
 * halves in step: the reference site ships a separate SVG per side, and its two
 * are already a few tenths of a unit out of true with each other.
 *
 * A shape is a list of parts drawn in order. A part is filled with the current
 * colour by default; `stroke` makes it a stroked line of that width instead,
 * which is how the thin stems are drawn without giving each one an outline path
 * of its own. A shape may also carry its own `viewBox` — the laurel is 19 wide
 * where the rest are 20 — since nothing downstream depends on the box size.
 *
 * The laurel is supplied artwork, kept at its original coordinates so it is
 * still diffable against the source it came from. Wheat and wings were computed
 * rather than eyeballed, so their grains sit evenly along the stem — see
 * shape-drafting.js to regenerate either.
 */

import { __ } from '@wordpress/i18n';

export const DEFAULT_VIEWBOX = '0 0 20 48';

export const SHAPES = {
  laurel: {
    label: __('Laurel', 'brandy-blocks'),
    viewBox: '0 0 19 48',
    parts: [
      {
        d: 'M8.24869 32.7769C7.74081 33.4666 7.38689 34.2573 7.21086 35.0955C7.03483 35.9338 7.04079 36.8 7.22832 37.6358C7.31554 38.0678 7.44296 38.4908 7.60894 38.8991C7.30121 38.5751 7.02587 38.2431 6.69385 37.9435C5.34209 36.6932 3.75699 35.7217 2.0293 35.0848C2.41344 37.2225 3.51975 39.1636 5.16329 40.5835C5.85473 41.2032 6.66506 41.6757 7.54494 41.9722C8.42482 42.2688 9.35584 42.3832 10.2813 42.3084C10.5981 40.6577 10.5706 38.9594 10.2004 37.3199C9.85766 35.6911 9.19422 34.1467 8.24869 32.7769Z',
      },
      {
        d: 'M5.09287 34.0077C6.00088 32.6049 6.62097 31.0355 6.91709 29.3908C7.21321 27.7462 7.17946 26.0591 6.81778 24.4276C6.10231 24.8737 5.48785 25.4643 5.01373 26.1615C4.5396 26.8588 4.21627 27.6473 4.0644 28.4767C3.97672 28.9084 3.9333 29.348 3.93483 29.7886C3.77287 29.3756 3.6433 28.9788 3.44894 28.5577C2.67277 26.8932 1.57139 25.4008 0.209674 24.1685C-0.243288 26.281 0.0425784 28.485 1.01949 30.4121C1.41738 31.2576 1.984 32.0127 2.68451 32.631C3.38501 33.2493 4.2046 33.7179 5.09287 34.0077Z',
      },
      {
        d: 'M3.44777 24.2811C4.84128 23.3269 6.01022 22.0805 6.8733 20.6288C7.7538 19.2186 8.34305 17.6463 8.60631 16.0048C7.77352 16.1556 6.98212 16.4811 6.28421 16.9598C5.5863 17.4386 4.99772 18.0598 4.55722 18.7824C4.31981 19.1547 4.11652 19.5478 3.94986 19.9567C3.94986 19.5194 3.99035 19.0821 3.94986 18.6367C3.83533 16.8051 3.36207 15.0139 2.55697 13.3647C1.98187 14.2558 1.54246 15.2274 1.25317 16.2477C0.989362 17.3059 0.90451 18.4008 1.00212 19.487C1.07003 20.4087 1.32135 21.3075 1.74132 22.1307C2.16129 22.954 2.74145 23.6851 3.44777 24.2811Z',
      },
      {
        d: 'M10.0176 12.5855C11.357 11.6002 12.4813 10.3519 13.3216 8.91699C12.488 8.75054 11.6287 8.76188 10.7998 8.95027C9.97089 9.13865 9.19108 9.49985 8.51132 10.0102C8.15031 10.2761 7.81664 10.5772 7.51524 10.9091C7.6772 10.4961 7.87156 10.0993 8.00923 9.67822C8.58983 7.93208 8.80753 6.08573 8.64898 4.25244C7.80086 4.88559 7.04102 5.62908 6.38959 6.46324C5.75607 7.34333 5.27432 8.32325 4.96431 9.36239C4.68854 10.2484 4.59304 11.1807 4.68345 12.1042C4.77385 13.0278 5.04833 13.9239 5.4907 14.7396C7.13637 14.3536 8.68008 13.6191 10.0176 12.5855Z',
      },
      {
        d: 'M13.5304 1.71951C11.9742 2.85485 10.8972 4.52954 10.5098 6.41645C12.1497 6.03841 13.6902 5.3151 15.0285 4.29473C16.3662 3.3083 17.4925 2.06363 18.3407 0.634355C17.5077 0.466253 16.6486 0.476024 15.8196 0.663029C14.9906 0.850035 14.2105 1.21006 13.5304 1.71951Z',
      },
      {
        d: 'M13.8714 46.5416C13.6356 46.3123 13.4138 46.0689 13.2074 45.8128C12.7782 45.2864 12.3976 44.7276 11.9765 44.1932L11.774 43.9502C11.5716 43.6992 11.1829 43.2538 10.8346 43.2214C10.4864 43.189 10.4945 43.6425 10.5755 43.9097C10.7025 44.3026 10.9155 44.6623 11.199 44.9625C11.5771 45.3383 11.9832 45.6848 12.4138 45.999C12.8083 46.3259 13.228 46.6211 13.669 46.8817L14.1711 47.1571C14.255 47.2051 14.3415 47.2484 14.4302 47.2867C14.495 47.2867 14.6894 47.2867 14.5841 47.1328L14.2764 46.8979L13.8714 46.5416Z',
      },
    ],
  },

  wheat: {
    label: __('Wheat', 'brandy-blocks'),
    parts: [
      { d: 'M14 46Q7 24 14 2', stroke: 1 },
      {
        d: 'M12.52 40.72Q11.23 37.6 7.86 37.78Q9.15 40.9 12.52 40.72ZM12.52 40.72Q14.27 40.15 13.92 38.35Q12.18 38.92 12.52 40.72ZM11.23 34.03Q10.09 30.35 6.24 30.19Q7.38 33.87 11.23 34.03ZM11.23 34.03Q13.28 33.57 13.07 31.48Q11.03 31.94 11.23 34.03ZM10.58 27.34Q9.74 23.3 5.66 22.74Q6.49 26.78 10.58 27.34ZM10.58 27.34Q12.81 27.06 12.81 24.82Q10.58 25.1 10.58 27.34ZM10.58 20.66Q10.14 16.55 6.12 15.6Q6.57 19.7 10.58 20.66ZM10.58 20.66Q12.83 20.59 13.04 18.35Q10.8 18.42 10.58 20.66ZM11.23 13.97Q11.18 10.11 7.53 8.87Q7.58 12.72 11.23 13.97ZM11.23 13.97Q13.32 14.11 13.72 12.05Q11.63 11.91 11.23 13.97ZM12.52 7.28Q12.79 3.92 9.72 2.53Q9.45 5.9 12.52 7.28ZM12.52 7.28Q14.34 7.57 14.85 5.81Q13.04 5.52 12.52 7.28Z',
      },
    ],
  },

  wings: {
    label: __('Wings', 'brandy-blocks'),
    parts: [
      {
        d: 'M18 46Q11.96 34.2 1.84 25.63Q7.88 37.43 18 46ZM18 46Q13.97 33.95 5.23 24.74Q9.26 36.79 18 46ZM18 46Q15.95 34.02 8.58 24.36Q10.63 36.34 18 46ZM18 46Q17.83 34.39 11.79 24.48Q11.97 36.08 18 46ZM18 46Q19.57 35.04 14.82 25.04Q13.24 36 18 46ZM18 46Q21.14 35.93 17.58 26Q14.44 36.07 18 46Z',
      },
    ],
  },

  brackets: {
    label: __('Brackets', 'brandy-blocks'),
    parts: [{ d: 'M15 3Q4 24 15 45', stroke: 2.4 }],
  },
};

export const DEFAULT_SHAPE = 'laurel';

/**
 * @param {string} shape Catalogue key.
 * @return {Array} Parts to draw, empty when the shape is unknown or switched off.
 */
export function getShapeParts(shape) {
  return SHAPES[shape]?.parts ?? [];
}

/**
 * A shape may be drawn in its own box — the laurel is 19 wide where the others
 * are 20. Nothing needs to know the number: the SVG sizes itself from its
 * height and takes its width from the viewBox's own ratio.
 *
 * @param {string} shape Catalogue key.
 * @return {string} viewBox attribute.
 */
export function getShapeViewBox(shape) {
  return SHAPES[shape]?.viewBox ?? DEFAULT_VIEWBOX;
}
